import React, {ChangeEvent, FormEvent, KeyboardEvent, ClipboardEvent, useEffect, useRef, useState} from 'react';
import {CircularProgress} from "@mui/material";

type PropType = {
    phoneNumber: string,
    setIsPhoneNumberSubmitted: React.Dispatch<React.SetStateAction<boolean>>;
    onVerifyOTP: (OTP: string) => void,
    InvalidOtpError: boolean,
    setInvalidOtpError: any,
    onSubmitPhoneNumber: () => void,
    otpFormReady: boolean,
    isOtpSubmitLoading: boolean,
    handleSendViaSMS?: () => void,
    smsAttempted?: boolean,
    canResendSms?: boolean,
    resendTimer?: number,
    isLoading?: boolean,
    otpDeliveryMethod?: "whatsapp" | "sms" | null,
    hideWrongNumber?: boolean,
}

const OTPForm: React.FC<PropType> = ({
                                         phoneNumber,
                                         onVerifyOTP,
                                         setIsPhoneNumberSubmitted,
                                         InvalidOtpError,
                                         setInvalidOtpError,
                                         onSubmitPhoneNumber,
                                         otpFormReady,
                                         isOtpSubmitLoading,
                                         handleSendViaSMS,
                                         smsAttempted = false,
                                         canResendSms = false,
                                         resendTimer = 0,
                                         isLoading = false,
                                         otpDeliveryMethod = null,
                                         hideWrongNumber = false,

                                     }) => {
    const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']);
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const [timer, setTimer] = useState<number>(15);
    const [canResend, setCanResend] = useState<boolean>(false);

    const firstInputRef = useRef<any>();
    useEffect(() => {
        if (otpFormReady && firstInputRef.current) {
            firstInputRef.current.focus();
        }
    }, [otpFormReady]);



    useEffect(() => {
        if (timer > 0) {
            const timerId = setInterval(() => {
                setTimer(prevTimer => prevTimer - 1);
            }, 1000);
            return () => clearInterval(timerId);
        } else {
            setCanResend(true);
        }
    }, [timer]);
    const handleInput = (e: ChangeEvent<HTMLInputElement>, index: number) => {
        const {value} = e.target;
        if (/^[0-9]$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (index < inputsRef.current.length - 1) {
                inputsRef.current[index + 1]?.focus();
            } else {
                inputsRef.current[index]?.blur();
            }
        }
    };


    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
        if (e.key === 'Backspace' || e.key === 'Delete') {
            const newOtp = [...otp];
            if (index > 0) {
                inputsRef.current[index - 1]?.focus();
                newOtp[index] = '';
                setOtp(newOtp);
            } else {
                newOtp[index] = '';
                setOtp(newOtp);
            }
        }
    };


    const handleFocus = (index: number) => {
        inputsRef.current[index]?.select();
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const text = e.clipboardData.getData('text');
        // Extract digits only
        const numericText = text.replace(/[^0-9]/g, '').slice(0, 6);
        
        if (numericText.length > 0) {
            const newOtp = [...otp];
            const digits = numericText.split('');
            
            digits.forEach((digit, i) => {
                if (i < 6) {
                    newOtp[i] = digit;
                }
            });
            
            setOtp(newOtp);
            
            // Focus the appropriate input after paste
            const focusIndex = Math.min(digits.length, 5);
            inputsRef.current[focusIndex]?.focus();
        }
    };

    const handleSubmit = (e?: FormEvent<HTMLFormElement>) => {
        if (e) {
            e.preventDefault();
        }

        if (otp.join('').length < 6) {
            setInvalidOtpError(true)
            return
        }
        setInvalidOtpError(false);
        onVerifyOTP(otp.join(''))
    };

    // -------------Resend Logic
    function resendLogic() {
        if (canResend) {
            onSubmitPhoneNumber()
            setTimer(30)
        }

    }

    useEffect(() => {
        if (otp.every(digit => digit !== '')) {
            handleSubmit();
        }
    }, [ otp]);

    return (
        <main className="relative  flex flex-col justify-center overflow-hidden">
            <div className="w-full max-w-6xl mx-auto  px-4 md:px-6 ">
                <div className="flex justify-center">
                    <div className="max-w-md mx-auto text-center bg-white px-4 sm:px-8  lg:py-10  ">
                        <header className="mb-8">
                            <h1 className="text-lg lg:text-2xl font-bold mb-1">Mobile Phone Verification</h1>
                            <p className="text-[12px] md:text-[15px] text-slate-500">
                                Enter the 6-digit verification code that was sent to mobile+91-{phoneNumber}.
                            </p>
                            {otpDeliveryMethod === "whatsapp" && (
                                <div className="mt-3 p-2 bg-green-50 border border-green-200 rounded-lg">
                                    <p className="text-[12px] md:text-[14px] text-green-800 font-medium">
                                        ✓ OTP sent via WhatsApp
                                    </p>
                                </div>
                            )}
                            {otpDeliveryMethod === "sms" && (
                                <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                                    <p className="text-[12px] md:text-[14px] text-blue-800 font-medium">
                                        ✓ OTP sent via SMS
                                    </p>
                                </div>
                            )}
                        </header>
                        <form onSubmit={handleSubmit}>
                            <div className="flex items-center justify-center gap-2  lg:gap-3">
                                {otp.map((value, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        autoComplete="one-time-code"
                                        className="w-10 h-10 lg:w-14 lg:h-14 text-center text-sm lg:text-2xl font-extrabold text-black text-slate-90 bg-slate-100 border hover:border-slate-200 appearance-none rounded lg:p-4 outline-none focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
                                        maxLength={1}
                                        value={value}
                                        onChange={(e) => handleInput(e, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        onFocus={() => handleFocus(index)}
                                        onPaste={handlePaste}
                                        ref={(el) => {
                                            inputsRef.current[index] = el;
                                            if (index === 0) {
                                                firstInputRef.current = el
                                            }
                                        }}
                                    />
                                ))}
                            </div>
                            {InvalidOtpError &&
                                <p className={"mt-2 text-red-600 text-sm "}>Invalid OTP, please input correct OTP</p>}
                            <div className="max-w-[260px] mx-auto mt-4">
                                <button
                                    type="submit"
                                    className={`w-full inline-flex justify-center whitespace-nowrap rounded-lg bg-cc_brand px-3.5 py-2.5 text-sm font-medium text-white shadow-sm shadow-indigo-950/10 hover:bg-indigo-600 focus:outline-none focus:ring focus:ring-indigo-300 focus-visible:outline-none focus-visible:ring focus-visible:ring-indigo-300 transition-colors duration-150 ${isOtpSubmitLoading ? "cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                    {isOtpSubmitLoading && <CircularProgress sx={{color: "white"}} size={20}/>}
                                    <p className={"ml-2"}>{isOtpSubmitLoading ? "Loading" : "Verify Account"}</p>
                                </button>
                            </div>
                        </form>
                        <div className="mt-5 flex flex-col items-center gap-3 text-xs lg:text-sm text-slate-500">
                            {/* Wrong Number — only shown on login */}
                            {!hideWrongNumber && (
                                <p
                                    className="font-medium text-cc_link hover:text-indigo-600 cursor-pointer"
                                    onClick={() => setIsPhoneNumberSubmitted(false)}
                                >
                                    Wrong Number?
                                </p>
                            )}

                            {/* Resend via WhatsApp */}
                            <p>
                                Didn't receive code?{' '}
                                <span
                                    className={`font-medium text-cc_link hover:text-indigo-600 ${canResend ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                                    onClick={resendLogic}
                                >
                                    {timer ? `Resend in ${timer}s` : 'Resend now'}
                                </span>
                            </p>

                            {/* SMS Fallback */}
                            {handleSendViaSMS && (
                                <>
                                    <div className="w-full flex items-center gap-2">
                                        <div className="flex-1 h-px bg-slate-200" />
                                        <span className="text-slate-400 text-xs">or</span>
                                        <div className="flex-1 h-px bg-slate-200" />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={handleSendViaSMS}
                                        disabled={isLoading || (smsAttempted && !canResendSms)}
                                        className="font-medium text-blue-600 hover:text-blue-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    >
                                        {smsAttempted && !canResendSms
                                            ? `Send via SMS (${resendTimer}s)`
                                            : 'Send via SMS'}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </main>
    );
};

export default OTPForm;
